const fontSizeStorageKey = 'outlineEditor.fontSize';
const lineHeightStorageKey = 'outlineEditor.lineHeight';
const defaultNodeFontSize = 16;
const defaultNodeFontScale = 1;
const minNodeFontScale = 0.8;
const maxNodeFontScale = 2.4;
const defaultNodeLineHeightScale = 1;
const minNodeLineHeightScale = 0.8;
const maxNodeLineHeightScale = 2;

function clampNodeFontScale(scale) {
  return Math.min(maxNodeFontScale, Math.max(minNodeFontScale, scale));
}

function storedNodeFontScale() {
  try {
    const storedValue = window.localStorage.getItem(fontSizeStorageKey);
    if (storedValue === null) {
      return defaultNodeFontScale;
    }
    const value = Number(storedValue);
    if (!Number.isFinite(value)) {
      return defaultNodeFontScale;
    }
    // 旧版は px 値（16〜28）で保存していたため、倍率へ読み替える。
    return clampNodeFontScale(value > maxNodeFontScale ? value / defaultNodeFontSize : value);
  } catch (error) {
    return defaultNodeFontScale;
  }
}

function saveNodeFontScale(scale) {
  try {
    window.localStorage.setItem(fontSizeStorageKey, clampNodeFontScale(scale).toFixed(1));
  } catch (error) {
    console.warn('Cannot save node font size.', error);
  }
}

function clearNodeFontSize() {
  try {
    window.localStorage.removeItem(fontSizeStorageKey);
  } catch (error) {
    console.warn('Cannot reset node font size.', error);
  }
}

export function createNodeFontSizeController({ slider, valueLabel, resetButton }) {
  function applyNodeFontScale(scale) {
    const nextScale = clampNodeFontScale(scale);
    document.documentElement.style.setProperty('--node-text-font-size', `${defaultNodeFontSize * nextScale}px`);
    if (slider) {
      slider.value = String(nextScale);
    }
    if (valueLabel) {
      valueLabel.textContent = `${Math.round(nextScale * 100)}%`;
    }
    if (resetButton) {
      resetButton.disabled = nextScale === defaultNodeFontScale;
    }
  }

  function applyStoredNodeFontSize() {
    applyNodeFontScale(storedNodeFontScale());
  }

  function setNodeFontScale(scale) {
    const nextScale = clampNodeFontScale(scale);
    applyNodeFontScale(nextScale);
    saveNodeFontScale(nextScale);
  }

  function resetNodeFontSize() {
    clearNodeFontSize();
    applyNodeFontScale(defaultNodeFontScale);
  }

  return { applyStoredNodeFontSize, setNodeFontScale, resetNodeFontSize };
}

function clampNodeLineHeightScale(scale) {
  return Math.min(maxNodeLineHeightScale, Math.max(minNodeLineHeightScale, scale));
}

function storedNodeLineHeightScale() {
  try {
    const storedValue = window.localStorage.getItem(lineHeightStorageKey);
    if (storedValue === null) {
      return defaultNodeLineHeightScale;
    }
    const value = Number(storedValue);
    if (!Number.isFinite(value)) {
      return defaultNodeLineHeightScale;
    }
    return clampNodeLineHeightScale(value);
  } catch (error) {
    return defaultNodeLineHeightScale;
  }
}

function saveNodeLineHeightScale(scale) {
  try {
    window.localStorage.setItem(lineHeightStorageKey, clampNodeLineHeightScale(scale).toFixed(1));
  } catch (error) {
    console.warn('Cannot save node line height.', error);
  }
}

function clearNodeLineHeight() {
  try {
    window.localStorage.removeItem(lineHeightStorageKey);
  } catch (error) {
    console.warn('Cannot reset node line height.', error);
  }
}

// 行間は文字サイズと同じ考え方で「倍率」を扱う。CSS の --node-line-height-scale を
// 各レイアウトの基準行間（1.5 など）に乗算して適用するため、見た目の倍率表示と一致する。
export function createNodeLineHeightController({ slider, valueLabel, resetButton }) {
  function applyNodeLineHeightScale(scale) {
    const nextScale = clampNodeLineHeightScale(scale);
    document.documentElement.style.setProperty('--node-line-height-scale', String(nextScale));
    if (slider) {
      slider.value = String(nextScale);
    }
    if (valueLabel) {
      valueLabel.textContent = `${Math.round(nextScale * 100)}%`;
    }
    if (resetButton) {
      resetButton.disabled = nextScale === defaultNodeLineHeightScale;
    }
  }

  function applyStoredNodeLineHeight() {
    applyNodeLineHeightScale(storedNodeLineHeightScale());
  }

  function setNodeLineHeightScale(scale) {
    const nextScale = clampNodeLineHeightScale(scale);
    applyNodeLineHeightScale(nextScale);
    saveNodeLineHeightScale(nextScale);
  }

  function resetNodeLineHeight() {
    clearNodeLineHeight();
    applyNodeLineHeightScale(defaultNodeLineHeightScale);
  }

  return { applyStoredNodeLineHeight, setNodeLineHeightScale, resetNodeLineHeight };
}
